/* eslint-disable no-nested-ternary */
/* eslint-disable react/destructuring-assignment */
import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import InfoIcon from './Components/icons/Info.svg';
import SECRETS from './secrets';
import { validIntGtZero, validIntGtEqZero } from './utils/validation-types';
import Alert from './Components/Alert';
import TopBar from './Components/NewTopBar';
import DatePicker from './Components/DatePicker';
import DialogUnitsList from './Components/DialogUnitsList';
import './Components/styles/ChecadorStyles.css';

const twoDigits = (number) => {
  if (number > 9) return number;
  return `0${number}`;
};

const formatToHM = (miliseconds) => {
  const hours = Math.floor(miliseconds / 1000 / 60 / 60);
  const minutes = Math.floor((miliseconds / 1000 / 60) - (hours * 60));
  return `${twoDigits(hours)}h${twoDigits(minutes)}m`;
};

const dialogUnitsListDefault = {
  open: false,
  userId: null,
  checkerName: '',
  dateValue: '',
  unitsList: [],
};

const ChecadorScreen = ({
  isLogged,
  token,
  logOut,
  secondary,
}) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(new Date());
  const [filter, setFilter] = useState('');
  const [recorridos, setRecorridos] = useState({});
  const [usuarios, setUsuarios] = useState([]);
  const [order, setOrder] = useState('nombre');
  const [asc, setAsc] = useState(true);
  const [dialogUnitsList, setDialogUnitsList] = useState({ ...dialogUnitsListDefault });

  const navigate = useNavigate();

  useEffect(() => {
    if (secondary === 'true') {
      navigate('/');
    }
  }, [secondary]);

  useEffect(() => {
    const getUsuarios = async () => {
      axios.defaults.baseURL = SECRETS.SERVERURL;
      axios.defaults.headers.common.Authorization = `bearer ${token}`;
      const response = await axios.get('/usuarios/organizacion')
        .catch((err) => console.error(err));
      setUsuarios(response.data);
    };
    getUsuarios();
  }, []);

  useEffect(() => {
    const getRecorridos = async () => {
      axios.defaults.baseURL = SECRETS.SERVERURL;
      axios.defaults.headers.common.Authorization = `bearer ${token}`;
      const fecha = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
      const response = await axios.get(`/controlrecorrido/by-checker/${fecha}`)
        .catch((err) => console.error(err));
      const recorridosDict = {};
      const recorridosData = response?.data?.travelsData ?? [];
      const unidadesData = response?.data?.unitsData ?? [];
      recorridosData.map((recorrido) => {
        if (recorridosDict[recorrido.idUsuario]) {
          if (!recorrido.cancelado) {
            const { min, max, unitsListArray } = recorridosDict[recorrido.idUsuario];
            const currentUnitId = recorrido.idUnidad;
            if (validIntGtZero(currentUnitId)) {
              const unitsListIndex = unitsListArray
                ?.findIndex((item) => item.idUnidad === currentUnitId) ?? -1;
              if (!validIntGtEqZero(unitsListIndex)) {
                const unidadesDataIndex = unidadesData
                  ?.findIndex((item) => item.idUnidad === currentUnitId) ?? -1;
                if (validIntGtEqZero(unidadesDataIndex)) {
                  unitsListArray.push(unidadesData[unidadesDataIndex]);
                }
              }
            }
            recorridosDict[recorrido.idUsuario].recorridos.push(recorrido);
            if (new Date(max.createdAt) - new Date(recorrido.createdAt) < 0) {
              recorridosDict[recorrido.idUsuario].max = recorrido;
            } else if (new Date(min.createdAt) - new Date(recorrido.createdAt) > 0) {
              recorridosDict[recorrido.idUsuario].min = recorrido;
            }
          }
        } else {
          const itemUnidad = unidadesData.find((item) => item.idUnidad === recorrido.idUnidad);
          recorridosDict[recorrido.idUsuario] = {
            min: recorrido,
            max: recorrido,
            recorridos: [recorrido],
            unitsListArray: [itemUnidad],
          };
        }
        return recorrido;
      });
      setRecorridos(recorridosDict);
    };
    getRecorridos();
  }, [date]);

  const searchFilter = (idUsuario) => {
    const removeTilde = (text) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const stringToSearch = filter.toLowerCase() || '';
    const matcher = new RegExp(`.*${removeTilde(stringToSearch)}.*`);
    const usuario = usuarios.find((u) => `${u.idUsuario}` === idUsuario);
    if (typeof usuario?.nombre !== 'undefined') {
      return (
        matcher.test(removeTilde(usuario.nombre.toLowerCase()))
        || matcher.test(removeTilde(recorridos[idUsuario]?.min?.nombreRuta.toLowerCase()))
        || matcher.test(removeTilde(recorridos[idUsuario]?.max?.nombreRuta.toLowerCase()))
      );
    }
    if (typeof recorridos[idUsuario]?.min !== 'undefined') {
      return (
        matcher.test(removeTilde(recorridos[idUsuario]?.min?.nombreRuta.toLowerCase()))
        || matcher.test(removeTilde(recorridos[idUsuario]?.max?.nombreRuta.toLowerCase()))
      );
    }
    return false;
  };

  const orderBy = (userIdA, userIdB) => {
    let a;
    let b;
    const recorridoA = recorridos[userIdA];
    const recorridoB = recorridos[userIdB];
    switch (order) {
      case 'nombre':
        a = (usuarios.find((u) => `${u.idUsuario}` === `${userIdA}`))?.nombre;
        b = (usuarios.find((u) => `${u.idUsuario}` === `${userIdB}`))?.nombre;
        if (asc) {
          return a.localeCompare(b);
        }
        return b.localeCompare(a);
      case 'salidas':
        a = recorridoA?.recorridos?.length ?? 0;
        b = recorridoB?.recorridos?.length ?? 0;
        if (asc) {
          return a - b;
        }
        return b - a;
      case 'unidades':
        a = recorridoA?.unitsListArray?.length ?? 0;
        b = recorridoB?.unitsListArray?.length ?? 0;
        if (asc) {
          return a - b;
        }
        return b - a;
      case 'entrada':
        a = new Date(recorridoA?.min?.createdAt);
        b = new Date(recorridoB?.min?.createdAt);
        if (asc) {
          return a - b;
        }
        return b - a;
      case 'salida':
        a = new Date(recorridoA?.max?.createdAt);
        b = new Date(recorridoB?.max?.createdAt);
        if (asc) {
          return a - b;
        }
        return b - a;
      case 'trabajo':
        a = new Date(recorridoA?.max?.createdAt) - new Date(recorridoA?.min?.createdAt);
        b = new Date(recorridoB?.max?.createdAt) - new Date(recorridoB?.min?.createdAt);
        if (asc) {
          return a - b;
        }
        return b - a;
      default:
        return userIdA - userIdB;
    }
  };

  const handleOrder = (column) => {
    if (column === order) {
      setAsc(!asc);
    } else {
      setOrder(column);
    }
  };

  const displayOrder = (column) => {
    if (column === order) {
      return asc ? '▲' : '▼';
    }
    return '';
  };

  const getCheckerInfoPerUnit = (userId) => {
    const userIdInt = parseInt(userId, 10);
    if (validIntGtEqZero(userIdInt)) {
      const userNameIndex = usuarios?.findIndex((item) => item.idUsuario === userIdInt) ?? -1;
      if (validIntGtEqZero(userNameIndex)) {
        let selectedUserId = null;
        let unitsList = [];
        const checkerName = usuarios?.[userNameIndex]?.nombre ?? '';
        for (let i = 0; i < Object.keys(recorridos).length; i += 1) {
          const objUserIdStr = Object.keys(recorridos)[i];
          const objUserId = parseInt(objUserIdStr, 10);
          if (validIntGtZero(objUserId)) {
            if (objUserId === parseInt(userIdInt, 10)) {
              selectedUserId = objUserId;
              break;
            }
          }
        }
        if (validIntGtZero(selectedUserId)) {
          unitsList = [...recorridos?.[selectedUserId]?.unitsListArray ?? []];
          const collator = new Intl.Collator([], { numeric: true });
          unitsList?.sort((a, b) => collator.compare(a.unidad, b.unidad));
        }
        setDialogUnitsList((prevValue) => ({
          ...prevValue,
          open: true,
          userId,
          checkerName,
          dateValue: date,
          unitsList,
        }));
      }
    }
  };

  const Tools = (
    <>
      <DatePicker label="Fecha de consulta" value={date} onChange={setDate} />
      <input type="text" name="buscar" id="buscar" value={filter} onChange={(e) => setFilter(e.target.value)} className="search-bar" placeholder="Buscar..." />
    </>
  );

  if (isLogged !== 'true') return <Navigate to="/" replace />;
  return (
    <>
      <Alert
        {...{
          open,
          setOpen,
        }}
      />
      <div className="page-body">
        <TopBar
          secondary={secondary}
          tools={Tools}
          handleLogOut={logOut}
          location="Checadores"
        />
        <div className="main-container">
          {Object.keys(recorridos).length > 0 && (
            <div className="relative-table">
              <table className="table-main">
                <thead className="table-thead">
                  <tr>
                    <th className="thead-th-checker" onClick={() => handleOrder('nombre')}>
                      {`${displayOrder('nombre')}Checador`}
                    </th>
                    <th className="thead-th-travels" onClick={() => handleOrder('salidas')}>
                      {`${displayOrder('salidas')}# Salidas`}
                    </th>
                    <th className="thead-th-units" onClick={() => handleOrder('unidades')}>
                      {`${displayOrder('unidades')}# Unidades`}
                    </th>
                    <th className="thead-th" onClick={() => handleOrder('entrada')}>
                      {`${displayOrder('entrada')}Hora Entrada`}
                    </th>
                    <th className="thead-th" onClick={() => handleOrder('salida')}>
                      {`${displayOrder('salida')}Hora Salida`}
                    </th>
                    <th className="thead-th-workhours" onClick={() => handleOrder('trabajo')}>
                      {`${displayOrder('trabajo')}Tiempo Trabajo`}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(recorridos)
                    .filter((usuarioId) => searchFilter(usuarioId))
                    .sort(orderBy)
                    .map((usuarioId) => (
                      <tr key={usuarioId} className="tbody-tr">
                        <td className="tbody-td-checker">{`${(usuarios.find((u) => `${u.idUsuario}` === usuarioId))?.nombre ?? ''}`}</td>
                        <td className="tbody-td-travels">
                          {`${recorridos[usuarioId].recorridos.length}`}
                        </td>
                        <td className="tbody-td">
                          <div className="units-list-cell">
                            <span className="units-count-span">{`${recorridos[usuarioId]?.unitsListArray?.length ?? 0}`}</span>
                            <button type="button" className="info-button" onClick={() => getCheckerInfoPerUnit(usuarioId)}>
                              <img src={InfoIcon} alt="Checadas del día" className="info-icon" />
                            </button>
                          </div>
                        </td>
                        <td className="tbody-td">
                          <div className="route-datetime">
                            <div>{`${(new Date(recorridos[usuarioId].min.createdAt)).toTimeString().split(' ')[0]}`}</div>
                            <div className="route-name">{`${recorridos[usuarioId].min.nombreRuta}`}</div>
                          </div>
                        </td>
                        <td className="tbody-td">
                          <div className="route-datetime">
                            <div>{`${(new Date(recorridos[usuarioId].max.createdAt)).toTimeString().split(' ')[0]}`}</div>
                            <div className="route-name">{`${recorridos[usuarioId].max.nombreRuta}`}</div>
                          </div>
                        </td>
                        <td className="tbody-td-workhours">{`${formatToHM(new Date(recorridos[usuarioId].max.createdAt) - new Date(recorridos[usuarioId].min.createdAt))}`}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
          {Object.keys(recorridos).length === 0 && (
            <h1 className="routes-not-found">
              No hay recorridos de esta fecha.
            </h1>
          )}
        </div>
      </div>
      <DialogUnitsList
        dialogUnitsList={dialogUnitsList}
        closeDialogUnitsList={() => {
          setDialogUnitsList(() => ({ ...dialogUnitsListDefault }));
        }}
      />
    </>
  );
};

export default ChecadorScreen;
